import { Badge, Tooltip } from 'antd'
import type { BadgeProps } from 'antd'
import { useTranslation } from 'react-i18next'
import type { DeviceStatus } from './attendanceApi'

/**
 * RANG — SABABGA qarab, og'irlikka qarab EMAS (panel bo'ylab bir xil
 * qoida: `NasHealthTag`, `StatusTag`).
 *
 *   · `ONLINE`      YASHIL  — qurilma javob berdi va parol to'g'ri
 *
 *   · `AUTH_FAILED` QIZIL   — ⚠️ va bu ATAYLAB `UNREACHABLE` dan
 *                             AJRATILGAN. Qurilma TIRIK, javob beryapti,
 *                             lekin foydalanuvchi nomi yoki parol mos
 *                             emas. Uni "o'chiq" deb ko'rsatish operatorni
 *                             butunlay boshqa yo'nalishda — kabel, quvvat,
 *                             tarmoq — qidirishga majbur qilardi, holbuki
 *                             tuzatish shu yerdagi formada bir daqiqalik
 *                             ish.
 *                             Qizil bo'lishining ikkinchi sababi amaliy:
 *                             Hikvision bir necha muvaffaqiyatsiz
 *                             urinishdan keyin manzilni ~30 daqiqaga
 *                             QULFLAYDI, ya'ni bu holat o'zi-o'zidan
 *                             tuzalmaydi va odam aralashuvini TALAB
 *                             QILADI — qizilning ta'rifi aynan shu.
 *
 *   · `UNREACHABLE` SARIQ   — javob yo'q. ⚠️ QIZIL EMAS: qurilma vaqtincha
 *                             o'chirilgan, VPN uzilgan yoki bino tokdan
 *                             chiqqan bo'lishi ODATIY hol va u o'zi
 *                             tiklanadi (`NasHealthTag.SILENT` dagi bilan
 *                             bir xil mulohaza). Har kechada qizil
 *                             ko'rsatiladigan panel bir haftada o'qilmay
 *                             qo'yadi.
 *
 *   · `UNKNOWN`     KULRANG — hali TEKSHIRILMAGAN. Yangi qo'shilgan
 *                             qurilma birinchi probe'gacha shu holatda
 *                             bo'ladi, ya'ni bu nosozlik belgisi EMAS.
 *                             Uni sariq qilish har yangi yozuvda yolg'on
 *                             ogohlantirish berardi.
 */
const STATUS_BADGE: Record<DeviceStatus, BadgeProps['status']> = {
  ONLINE: 'success',
  AUTH_FAILED: 'error',
  UNREACHABLE: 'warning',
  UNKNOWN: 'default',
}

interface DeviceStatusTagProps {
  status: DeviceStatus
  /** Serverdagi oxirgi xato matni — bo'lsa tooltip'ga qo'shiladi */
  lastError?: string | null
  /** Ketma-ket muvaffaqiyatsizliklar soni */
  consecutiveFailures?: number | null
}

/**
 * Jadval katagi — holat + SABABI tooltip'da.
 *
 * ⚠️ Yorliqning o'zi yolg'iz yetmaydi: "Yetib bo'lmadi" operatorga nima
 * qilish kerakligini aytmaydi. Tooltip har holat uchun nima
 * tekshirilishini aytadi, va serverdagi oxirgi xato matni bo'lsa —
 * o'shani ham ko'rsatadi (u qurilmaning O'Z javobi, biz uni qayta
 * yozsak ma'lumot yo'qolardi).
 */
export function DeviceStatusTag({
  status,
  lastError,
  consecutiveFailures,
}: DeviceStatusTagProps) {
  const { t } = useTranslation()

  const lines = [t(`attendance.statusHint.${status}`)]
  // Ketma-ket sonini faqat MA'NOLI bo'lganda ko'rsatamiz: bitta
  // muvaffaqiyatsizlik hali xulosa emas, o'ntasi esa boshqa gap.
  if (status !== 'ONLINE' && consecutiveFailures && consecutiveFailures > 1) {
    lines.push(t('attendance.consecutiveFailures', { count: consecutiveFailures }))
  }
  // ⚠️ BUG-033 — XOM MATN YORLIQLANADI (2026-08-16, ikkinchi qavat).
  //    `DeviceProbeModal` tuzatilgandi, bu qator esa AYNAN o'sha
  //    ekranning RO'YXATIDA qolgandi va u xom Java matni:
  //    `HikOperations#call` → `"qurilmaga yetib bo'lmadi: " + e`, ya'ni
  //    tooltipda `java.net.SocketTimeoutException` turardi.
  //    Matn O'CHIRILMAYDI — u nosozlikni topadigan yagona ip bo'lishi
  //    mumkin; u BELGILANADI, shunda operator qaysi qator o'ziga,
  //    qaysi biri qo'llab-quvvatlashga tegishli ekanini biladi.
  if (lastError) lines.push(`${t('attendance.technicalDetail')}: ${lastError}`)

  return (
    // ⚠️ Tooltip matni JSX bo'lib beriladi, "\n" bilan birlashtirilgan
    // satr sifatida EMAS: antd tooltip ichida `white-space` odatiy, ya'ni
    // qator uzilishi yo'qoladi va uch jumla bir-biriga yopishib qolardi.
    <Tooltip
      title={lines.map((line, index) => (
        <div key={index}>{line}</div>
      ))}
    >
      <Badge status={STATUS_BADGE[status]} text={t(`attendance.status.${status}`)} />
    </Tooltip>
  )
}
